// Broker Channel Batch (WS5) — staff-side clickable pipeline stepper.
// Visually modeled on the shared design-system <StatusStepper> pill style
// (deliberately NOT a modification of it — that component stays the inert
// portal-lifecycle indicator). Numbered 1–4 (Prospect → Qualified →
// Onboarding → Certified): completed stages get checks, the current stage is
// highlighted, the NEXT stage carries a "Next" hint and is the emphasized
// click target. With Brokers.Qualify the click targets are the immediate
// NEXT stage (forward is single-step — stages can't be skipped) plus any
// EARLIER stage (a backward move can retreat to any prior stage); without
// the permission, or when the broker sits in an archive status, the stepper
// renders inert. A graduated broker (teamId set) renders fully completed
// with a "Graduated — {teamName}" chip.

import { Badge } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import {
  PIPELINE_STATUSES,
  isArchiveStatus,
  type BrokerStatus,
} from "@/api/brokers";

export interface BrokerStageStepperProps {
  status: BrokerStatus;
  teamId?: string | null;
  teamName?: string | null;
  /** Brokers.Qualify — without it the stepper is inert. */
  canQualify: boolean;
  onStageClick: (target: BrokerStatus) => void;
  /** Rendered beside the stepper when the broker is archived. */
  archiveBadge?: React.ReactNode;
}

export function BrokerStageStepper({
  status,
  teamId,
  teamName,
  canQualify,
  onStageClick,
  archiveBadge,
}: BrokerStageStepperProps) {
  const archived = isArchiveStatus(status);
  const graduated = !!teamId;
  const currentIndex = graduated
    ? PIPELINE_STATUSES.length - 1
    : PIPELINE_STATUSES.indexOf(status); // -1 when archived
  const interactive = canQualify && !archived;

  return (
    <div
      className="flex flex-wrap items-center gap-space-2"
      data-testid="broker-stage-stepper"
    >
      <ol
        className="flex flex-wrap items-center gap-space-2 list-none p-0 m-0"
        aria-label="Broker pipeline stage"
      >
        {PIPELINE_STATUSES.map((stage, idx) => {
          const isComplete = idx < currentIndex || (idx === currentIndex && graduated);
          const isActive = idx === currentIndex && !graduated;
          const isNext = interactive && !graduated && idx === currentIndex + 1;
          // Forward is single-step (only the immediate next stage — stages
          // can't be skipped ahead); backward can retreat to ANY earlier
          // stage. The current stage is never its own target.
          const clickable =
            interactive && (idx === currentIndex + 1 || idx < currentIndex);

          const pill = (
            <span
              className={cn(
                "inline-flex items-center gap-space-1 rounded-pill border px-space-2 py-[3px] text-caption transition-colors duration-fast ease-standard",
                isActive &&
                  "border-gold bg-gold text-onyx font-semibold ring-1 ring-gold",
                isComplete &&
                  "border-status-success bg-status-success/15 text-status-success",
                !isActive && !isComplete && !isNext &&
                  "border-border-light bg-white-warm text-gray-mid",
                isNext &&
                  "border-gold bg-gold/10 text-onyx font-semibold",
                clickable && "cursor-pointer hover:border-gold-shadow",
              )}
              aria-current={isActive ? "step" : undefined}
              data-step={stage}
              data-state={isActive ? "active" : isComplete ? "complete" : "upcoming"}
            >
              <span className="inline-block w-4 text-center" aria-hidden="true">
                {isComplete ? "✓" : idx + 1}
              </span>
              {stage}
              {isNext && (
                <span className="font-display text-label uppercase tracking-label text-gold-shadow">
                  Next
                </span>
              )}
            </span>
          );

          return (
            <li key={stage} className="flex items-center gap-space-2 shrink-0">
              {clickable ? (
                <button
                  type="button"
                  className="bg-transparent border-0 p-0 m-0"
                  onClick={() => onStageClick(stage)}
                  aria-label={`Move to ${stage}`}
                  data-testid={`broker-stage-${stage.toLowerCase()}`}
                >
                  {pill}
                </button>
              ) : (
                <span data-testid={`broker-stage-${stage.toLowerCase()}`}>{pill}</span>
              )}
              {idx < PIPELINE_STATUSES.length - 1 && (
                <span className="text-caption text-gray-mid" aria-hidden="true">
                  →
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {graduated && (
        <Badge status="finalized" data-testid="broker-graduated-chip">
          Graduated{teamName ? ` — ${teamName}` : ""}
        </Badge>
      )}
      {archived && archiveBadge}
    </div>
  );
}
